import type { SdkKeyListItem, ViewSummary } from '@gonfalon/openapi/types';

/**
 * Discriminator for the kind of payload resource a confirmation dialog is
 * surfacing. The two values match the resource-summary fields exposed by
 * `ViewSummary`, so callers can use it to read counts and format unit copy
 * (`flag` / `segment`) without re-deriving from string literals.
 */
export type ChangeableResourceType = 'flags' | 'segments';

/**
 * Pull the count of the given resource type out of a single `ViewSummary`.
 * Pure indirection — keeps the `flags` vs `segments` field choice in one place.
 */
export function countForResource(view: ViewSummary, resourceType: ChangeableResourceType): number {
  return resourceType === 'flags' ? view.resourceSummary.flagCount : view.resourceSummary.segmentCount;
}

/**
 * Sum the given resource type across an array of `ViewSummary`s. Returns
 * `undefined` when the input array itself is `undefined` so callers can
 * branch into an approximation path (see the `viewSummaries`-absent fallback
 * inside the confirmation dialogs).
 */
export function sumResource(views: ViewSummary[], resourceType: ChangeableResourceType): number;
export function sumResource(views: ViewSummary[] | undefined, resourceType: ChangeableResourceType): number | undefined;
export function sumResource(
  views: ViewSummary[] | undefined,
  resourceType: ChangeableResourceType,
): number | undefined {
  if (!views) {
    return undefined;
  }
  return views.reduce((acc, v) => acc + countForResource(v, resourceType), 0);
}

/**
 * `SdkKeyListItem` from the list endpoint doesn't expose `viewSummaries` yet —
 * the override at `packages/openapi/src/schema-overrides.ts` adds it to the
 * single-key `SdkKey` schema only. We accept it defensively here so callers
 * render precise before/after counts as soon as the BE surfaces it; until then
 * the `viewSummaries`-absent path falls back to an upper-bound approximation.
 */
export type SdkKeyWithViewSummaries = SdkKeyListItem & {
  viewSummaries?: ViewSummary[];
};

export type RenderableSdkKey = {
  key: string;
  name: string;
  /** Sum of the resource type across the SDK key's attached views. `undefined` when the field isn't populated. */
  beforeCount: number | undefined;
};

export type SdkKeyEnvGroup = {
  envKey: string;
  envName: string;
  envColor: string;
  keys: RenderableSdkKey[];
};

/**
 * Group an SDK key list by environment, preserving server response order
 * within each env. Default keys are dropped defensively (they cannot be
 * payload-filtered) and orphan keys missing `environmentSummary`
 * are skipped.
 */
export function groupSdkKeysByEnvironment(
  sdkKeys: SdkKeyWithViewSummaries[],
  resourceType: ChangeableResourceType,
): SdkKeyEnvGroup[] {
  const groupsByEnv = new Map<string, SdkKeyEnvGroup>();

  for (const sdkKey of sdkKeys) {
    if (sdkKey.isDefault) {
      continue;
    }
    const envSummary = sdkKey.environmentSummary;
    if (!envSummary) {
      continue;
    }

    let group = groupsByEnv.get(envSummary.key);
    if (!group) {
      group = {
        envKey: envSummary.key,
        envName: envSummary.name,
        envColor: envSummary.color,
        keys: [],
      };
      groupsByEnv.set(envSummary.key, group);
    }

    group.keys.push({
      key: sdkKey.key,
      name: sdkKey.name,
      beforeCount: sumResource(sdkKey.viewSummaries, resourceType),
    });
  }

  return Array.from(groupsByEnv.values());
}
